'use client';

import {usePathname} from 'next/navigation';
import {useContext} from 'react';

import ProfileTypes from '@/enums/profileTypes';
import type NotificationStore from '@/store/NotificationStore';
import type PlatformDataStore from '@/store/PlatformDataStore';
import type UserStore from '@/store/UserStore';
import {GlobalStoreContext} from '@/utils/context/GlobalStoreProvider';

import {AccountSecurityStoreContext, CompanyStoreContext} from '../context/createContexts';
import {GlobalLoaderContext} from '../context/LoadingWrapper';
import {EntityType} from '../enums';
import GlobalStoresIdentifier from '../enums/store-identifier';

export function useGlobalStoreContext() {
  return useContext(GlobalStoreContext);
}
export function useGlobalLoaderContext() {
  return useContext(GlobalLoaderContext);
}
export function useUserSecurityStoreContext() {
  return useContext(AccountSecurityStoreContext);
}

export function useUserStore() {
  const globalStore = useGlobalStoreContext();
  const userStore = globalStore.getStore<UserStore>(GlobalStoresIdentifier.USER_STORE);
  return userStore;
}

export function useNotificationStore() {
  const globalStore = useGlobalStoreContext();
  const notificationStore = globalStore.getStore<NotificationStore>(GlobalStoresIdentifier.NOTIFICATION_STORE);
  return notificationStore;
}

export function useEntityContext() {
  const {company} = useContext(CompanyStoreContext);
  const {user} = useUserStore();
  const pathName = usePathname();
  return company
    ? {...company, type: EntityType.ORG}
    : {...user, type: pathName.includes('platform') ? EntityType.PLATFORM : EntityType.USER};
}

export function usePlatformDataStore() {
  const globalStore = useGlobalStoreContext();
  const platformDataStore = globalStore.getStore<PlatformDataStore>(GlobalStoresIdentifier.PLATFORM_STORE);
  return platformDataStore;
}
export function useActiveContextType() {
  const {company} = useContext(CompanyStoreContext);
  const pathName = usePathname();
  return company ? ProfileTypes.COMPANY : pathName.includes('platform') ? ProfileTypes.PLATFORM : ProfileTypes.PERSONAL;
}
